import dagre from "dagre";

export function layoutFlowchart(parsedData) {
  const { nodes, edges, direction = "TD", subgraphs = [] } = parsedData;

  const g = new dagre.graphlib.Graph({ compound: true });
  g.setGraph({
    rankdir: direction === "TD" ? "TB" : direction,
    ranksep: 70,
    nodesep: 50,
    edgesep: 20,
    marginx: 50,
    marginy: 50,
  });
  g.setDefaultEdgeLabel(() => ({}));

  // Add subgraph groups
  subgraphs.forEach((sg) => {
    g.setNode(sg.id, { label: sg.label, clusterLabelPos: "top" });
  });

  // Add nodes
  nodes.forEach((node) => {
    const charWidth = node.shape === "diamond" ? 12 : 9;
    const width = Math.max(120, node.label.length * charWidth + 40);
    const height = node.shape === "diamond" ? 70 : 44;
    g.setNode(node.id, { ...node, width, height });

    // Assign to subgraph
    subgraphs.forEach((sg) => {
      if (sg.nodeIds.includes(node.id)) {
        g.setParent(node.id, sg.id);
      }
    });
  });

  // Add edges
  edges.forEach((edge) => {
    g.setEdge(edge.source, edge.target, {
      label: edge.label || "",
      style: edge.style,
    });
  });

  dagre.layout(g);

  const layoutNodes = nodes.map((n) => {
    const info = g.node(n.id);
    return { ...n, x: info.x, y: info.y, width: info.width, height: info.height };
  });

  const layoutEdges = edges.map((e) => {
    const info = g.edge(e.source, e.target);
    return { ...e, points: info?.points || [] };
  });

  const layoutSubgraphs = subgraphs
    .map((sg) => {
      const sgInfo = g.node(sg.id);
      return sgInfo
        ? { ...sg, x: sgInfo.x, y: sgInfo.y, width: sgInfo.width, height: sgInfo.height }
        : null;
    })
    .filter(Boolean);

  const graphInfo = g.graph();

  return {
    nodes: layoutNodes,
    edges: layoutEdges,
    subgraphs: layoutSubgraphs,
    width: (graphInfo.width || 600) + 100,
    height: (graphInfo.height || 400) + 100,
  };
}

export function layoutSequence(parsedData) {
  const { actors, messages } = parsedData;
  const actorSpacing = 200;
  const messageSpacing = 55;
  const startY = 60;
  const totalHeight = startY + 80 + messages.length * messageSpacing + 40;

  const layoutActors = actors.map((actor, i) => ({
    ...actor,
    x: 120 + i * actorSpacing,
    y: startY,
  }));

  const actorMap = {};
  layoutActors.forEach((a) => {
    actorMap[a.id] = a;
  });

  const layoutMessages = messages.map((msg, i) => ({
    ...msg,
    y: startY + 70 + i * messageSpacing,
    fromX: actorMap[msg.from]?.x || 0,
    toX: actorMap[msg.to]?.x || 0,
  }));

  return {
    actors: layoutActors,
    messages: layoutMessages,
    width: Math.max(500, actors.length * actorSpacing + 140),
    height: totalHeight,
  };
}


export function layoutClassDiagram(parsedData) {
  const { classes, relationships } = parsedData;
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", ranksep: 60, nodesep: 50, marginx: 50, marginy: 50 });
  g.setDefaultEdgeLabel(() => ({}));

  const classDetails = classes.map((cls) => {
    const lineHeight = 20;
    const headerH = 34;
    const pad = 14;
    const propH = cls.properties.length > 0 ? cls.properties.length * lineHeight + pad : 0;
    const methH = cls.methods.length > 0 ? cls.methods.length * lineHeight + pad : 0;
    const height = headerH + propH + methH + pad;
    const maxLen = Math.max(
      cls.name.length * 10 + 40,
      ...cls.properties.map((p) => `${p.visibility}${p.name}: ${p.type}`.length * 7.5 + 30),
      ...cls.methods.map((m) => `${m.visibility}${m.name}(${m.params}): ${m.returnType}`.length * 7.5 + 30)
    );
    const width = Math.max(180, maxLen);
    return { ...cls, width, height, headerH, lineHeight };
  });

  classDetails.forEach((cls) => g.setNode(cls.name, { width: cls.width, height: cls.height }));
  relationships.forEach((rel) => g.setEdge(rel.from, rel.to, { label: rel.label }));
  dagre.layout(g);

  const layoutClasses = classDetails.map((cls) => {
    const node = g.node(cls.name);
    return { ...cls, x: node.x, y: node.y };
  });

  const layoutRels = relationships.map((rel) => {
    const info = g.edge(rel.from, rel.to);
    return { ...rel, points: info?.points || [] };
  });

  const graphInfo = g.graph();
  return {
    classes: layoutClasses,
    relationships: layoutRels,
    width: (graphInfo.width || 600) + 100,
    height: (graphInfo.height || 400) + 100,
  };
}

export function layoutGantt(parsedData) {
  const { title, sections } = parsedData;
  const taskHeight = 28;
  const taskGap = 8;
  const sectionHeaderH = 28;
  const leftMargin = 180;
  const topMargin = 60;
  const dayWidth = 28;

  const allTasks = sections.flatMap((s) => s.tasks);
  if (allTasks.length === 0) {
    return { title, sections: [], width: 600, height: 200, dayWidth, leftMargin, topMargin, totalDays: 0, startDate: null };
  }

  const startDates = allTasks.map((t) => new Date(t.start));
  const endDates = allTasks.map((t) => { const d = new Date(t.start); d.setDate(d.getDate() + t.duration); return d; });
  const minDate = new Date(Math.min(...startDates));
  const maxDate = new Date(Math.max(...endDates));
  const totalDays = Math.ceil((maxDate - minDate) / 86400000) + 2;

  let currentY = topMargin;
  const layoutSections = sections.map((section) => {
    const sectionY = currentY;
    currentY += sectionHeaderH;
    const layoutTasks = section.tasks.map((task) => {
      const startDay = Math.ceil((new Date(task.start) - minDate) / 86400000);
      const y = currentY;
      currentY += taskHeight + taskGap;
      return { ...task, startDay, y };
    });
    return { ...section, y: sectionY, tasks: layoutTasks };
  });

  return { title, sections: layoutSections, width: leftMargin + totalDays * dayWidth + 40, height: currentY + 20, dayWidth, leftMargin, topMargin, totalDays, startDate: minDate };
}

export function layoutKanban(parsedData) {
  const { columns } = parsedData;
  const colWidth = 220;
  const colGap = 16;
  const cardH = 40;
  const cardGap = 8;
  const headerH = 44;
  const pad = 16;

  const layoutColumns = columns.map((col, i) => ({
    ...col,
    x: pad + i * (colWidth + colGap),
    width: colWidth,
    height: headerH + col.items.length * (cardH + cardGap) + pad,
    items: col.items.map((item, j) => ({ text: item, y: headerH + j * (cardH + cardGap) })),
  }));

  const maxH = Math.max(...layoutColumns.map((c) => c.height), 200);
  return { columns: layoutColumns, width: pad * 2 + columns.length * (colWidth + colGap) - colGap, height: maxH + pad * 2, colWidth, cardH, headerH, pad };
}

export function layoutQuadrant(parsedData) {
  const size = 460;
  const margin = 80;
  return { ...parsedData, size, margin, width: size + margin * 2, height: size + margin * 2 + 20 };
}

export function layoutRequirements(parsedData) {
  const { requirements, relationships } = parsedData;
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", ranksep: 60, nodesep: 40, marginx: 50, marginy: 50 });
  g.setDefaultEdgeLabel(() => ({}));

  requirements.forEach((req) => {
    const w = Math.max(200, req.name.length * 9 + 40);
    g.setNode(req.id, { ...req, width: w, height: 80 });
  });
  relationships.forEach((rel) => g.setEdge(rel.from, rel.to, { label: rel.type }));
  dagre.layout(g);

  const layoutReqs = requirements.map((req) => {
    const node = g.node(req.id);
    return node ? { ...req, x: node.x, y: node.y, width: node.width, height: node.height } : req;
  });
  const layoutRels = relationships.map((rel) => {
    const info = g.edge(rel.from, rel.to);
    return { ...rel, points: info?.points || [] };
  });
  const graphInfo = g.graph();
  return { requirements: layoutReqs, relationships: layoutRels, width: (graphInfo.width || 600) + 100, height: (graphInfo.height || 400) + 100 };
}

export function layoutJourney(parsedData) {
  const { title, sections } = parsedData;
  const stepW = 130;
  const sectionGap = 24;
  const topM = 60;
  const leftM = 40;

  let currentX = leftM;
  const layoutSections = sections.map((section) => {
    const sectionX = currentX;
    const steps = section.steps.map((step) => {
      const x = currentX;
      currentX += stepW;
      return { ...step, x, y: topM + 40 };
    });
    currentX += sectionGap;
    return { ...section, x: sectionX, steps, width: steps.length * stepW };
  });

  return { title, sections: layoutSections, width: currentX + leftM, height: topM + 160, stepW, topM };
}

// --- NEW LAYOUT FUNCTIONS ---

export function layoutPie(parsedData) {
  const { title, slices } = parsedData;
  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const centerX = 250;
  const centerY = 200;
  const radius = 150;
  
  let startAngle = -Math.PI / 2;
  const layoutSlices = slices.map((slice) => {
    const angle = (slice.value / total) * Math.PI * 2;
    const endAngle = startAngle + angle;
    const result = {
      ...slice,
      percentage: ((slice.value / total) * 100).toFixed(1),
      startAngle,
      endAngle,
      midAngle: startAngle + angle / 2
    };
    startAngle = endAngle;
    return result;
  });

  return { title, slices: layoutSlices, centerX, centerY, radius, width: 500, height: 400 };
}

export function layoutMindmap(parsedData) {
  const { root } = parsedData;
  const nodeWidth = 140;
  const nodeHeight = 36;
  const horizontalGap = 60;
  const verticalGap = 20;

  function calculateSubtreeHeight(node) {
    if (!node.children || node.children.length === 0) return nodeHeight;
    return node.children.reduce((sum, child) => sum + calculateSubtreeHeight(child) + verticalGap, -verticalGap);
  }

  function layoutNode(node, x, y, direction = 1) {
    const layoutChildren = [];
    let childY = y - calculateSubtreeHeight(node) / 2 + nodeHeight / 2;
    
    (node.children || []).forEach(child => {
      const childHeight = calculateSubtreeHeight(child);
      const childX = x + direction * (nodeWidth + horizontalGap);
      const childLayout = layoutNode(child, childX, childY + childHeight / 2, direction);
      layoutChildren.push(childLayout);
      childY += childHeight + verticalGap;
    });

    return { ...node, x, y, width: nodeWidth, height: nodeHeight, children: layoutChildren };
  }

  const totalHeight = calculateSubtreeHeight(root);
  const layoutRoot = layoutNode(root, 300, totalHeight / 2 + 50);

  return { root: layoutRoot, width: 800, height: Math.max(400, totalHeight + 100) };
}

export function layoutSankey(parsedData) {
  const { title, nodes, flows } = parsedData;
  const nodeWidth = 20;
  const width = 700;
  const height = 400;
  const margin = 60;

  // Simple left-to-right layout
  const sourceNodes = new Set(flows.map(f => f.source));
  const targetNodes = new Set(flows.map(f => f.target));
  const leftNodes = [...sourceNodes].filter(n => !targetNodes.has(n));
  const rightNodes = [...targetNodes].filter(n => !sourceNodes.has(n));
  const middleNodes = nodes.filter(n => !leftNodes.includes(n) && !rightNodes.includes(n));

  const nodeY = {};
  const nodeX = {};
  let y = margin;
  leftNodes.forEach(n => { nodeX[n] = margin; nodeY[n] = y; y += 60; });
  y = margin;
  middleNodes.forEach(n => { nodeX[n] = width / 2; nodeY[n] = y; y += 60; });
  y = margin;
  rightNodes.forEach(n => { nodeX[n] = width - margin - nodeWidth; nodeY[n] = y; y += 60; });

  const layoutNodes = nodes.map(n => ({
    id: n,
    x: nodeX[n] || margin,
    y: nodeY[n] || margin,
    width: nodeWidth,
    height: 40
  }));

  const layoutFlows = flows.map(f => ({
    ...f,
    sourceX: (nodeX[f.source] || margin) + nodeWidth,
    sourceY: (nodeY[f.source] || margin) + 20,
    targetX: nodeX[f.target] || width - margin - nodeWidth,
    targetY: (nodeY[f.target] || margin) + 20
  }));

  return { title, nodes: layoutNodes, flows: layoutFlows, width, height };
}

export function layoutTimeline(parsedData) {
  const { title, events } = parsedData;
  const width = Math.max(800, events.length * 180);
  const height = 300;
  const lineY = 150;

  const layoutEvents = events.map((event, i) => ({
    ...event,
    x: 100 + i * 160,
    y: lineY,
    above: i % 2 === 0
  }));

  return { title, events: layoutEvents, width, height, lineY };
}

export function layoutState(parsedData) {
  const { states, transitions } = parsedData;
  
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "LR", ranksep: 80, nodesep: 50, marginx: 50, marginy: 50 });
  g.setDefaultEdgeLabel(() => ({}));

  states.forEach(state => {
    const w = state.type === "start" || state.type === "end" ? 30 : Math.max(100, state.label.length * 10);
    const h = state.type === "start" || state.type === "end" ? 30 : 50;
    g.setNode(state.id, { ...state, width: w, height: h });
  });

  transitions.forEach(t => {
    g.setEdge(t.from, t.to, { label: t.label });
  });

  dagre.layout(g);

  const layoutStates = states.map(s => {
    const info = g.node(s.id);
    return { ...s, x: info.x, y: info.y, width: info.width, height: info.height };
  });

  const layoutTransitions = transitions.map(t => {
    const info = g.edge(t.from, t.to);
    return { ...t, points: info?.points || [] };
  });

  const graphInfo = g.graph();
  return { states: layoutStates, transitions: layoutTransitions, width: (graphInfo.width || 500) + 100, height: (graphInfo.height || 300) + 100 };
}

export function layoutER(parsedData) {
  const { entities, relationships } = parsedData;
  const entityWidth = 200;
  const attrHeight = 24;
  const headerHeight = 36;

  const layoutEntities = entities.map((entity, i) => {
    const height = headerHeight + entity.attributes.length * attrHeight + 10;
    const x = 80 + (i % 3) * (entityWidth + 80);
    const y = 60 + Math.floor(i / 3) * 200;
    return { ...entity, x, y, width: entityWidth, height };
  });

  const entityMap = {};
  layoutEntities.forEach(e => { entityMap[e.name] = e; });

  const layoutRelationships = relationships.map(rel => {
    const from = entityMap[rel.from];
    const to = entityMap[rel.to];
    return {
      ...rel,
      fromX: from ? from.x + from.width : 0,
      fromY: from ? from.y + from.height / 2 : 0,
      toX: to ? to.x : 0,
      toY: to ? to.y + to.height / 2 : 0
    };
  });

  const maxX = Math.max(...layoutEntities.map(e => e.x + e.width)) + 80;
  const maxY = Math.max(...layoutEntities.map(e => e.y + e.height)) + 80;

  return { entities: layoutEntities, relationships: layoutRelationships, width: maxX, height: maxY };
}

export function layoutBlock(parsedData) {
  const { blocks } = parsedData;
  const blockWidth = 200;
  const blockHeight = 120;

  const layoutBlocks = blocks.map((block, i) => ({
    ...block,
    x: 80 + (i % 3) * (blockWidth + 40),
    y: 60 + Math.floor(i / 3) * (blockHeight + 40),
    width: blockWidth,
    height: blockHeight
  }));

  const maxX = Math.max(...layoutBlocks.map(b => b.x + b.width)) + 80;
  const maxY = Math.max(...layoutBlocks.map(b => b.y + b.height)) + 80;

  return { blocks: layoutBlocks, width: Math.max(600, maxX), height: Math.max(300, maxY) };
}

export function layoutC4(parsedData) {
  const { title, elements, relationships } = parsedData;

  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", ranksep: 100, nodesep: 80, marginx: 60, marginy: 60 });
  g.setDefaultEdgeLabel(() => ({}));

  elements.forEach(elem => {
    const w = Math.max(180, elem.label.length * 10);
    const h = elem.description ? 80 : 60;
    g.setNode(elem.id, { ...elem, width: w, height: h });
  });

  relationships.forEach(rel => {
    g.setEdge(rel.from, rel.to, { label: rel.label });
  });

  dagre.layout(g);

  const layoutElements = elements.map(e => {
    const info = g.node(e.id);
    return { ...e, x: info.x, y: info.y, width: info.width, height: info.height };
  });

  const layoutRelationships = relationships.map(rel => {
    const info = g.edge(rel.from, rel.to);
    return { ...rel, points: info?.points || [] };
  });

  const graphInfo = g.graph();
  return { title, elements: layoutElements, relationships: layoutRelationships, width: (graphInfo.width || 600) + 120, height: (graphInfo.height || 400) + 120 };
}

export function layoutGit(parsedData) {
  const { branches, commits } = parsedData;
  const width = Math.max(600, commits.length * 100 + 150);
  const height = Math.max(200, branches.length * 80 + 100);

  const branchY = {};
  branches.forEach((b, i) => { branchY[b] = 60 + i * 70; });

  const layoutCommits = commits.map((commit, i) => ({
    ...commit,
    x: 100 + i * 90,
    y: branchY[commit.branch] || 60
  }));

  const layoutBranches = branches.map(b => ({
    name: b,
    y: branchY[b]
  }));

  return { branches: layoutBranches, commits: layoutCommits, width, height };
}

export function layoutPacket(parsedData) {
  const { title, fields } = parsedData;
  const bitsPerRow = 32;
  const bitWidth = 20;
  const rowHeight = 40;
  const margin = 60;

  const layoutFields = fields.map(field => {
    const row = Math.floor(field.start / bitsPerRow);
    const startBit = field.start % bitsPerRow;
    const endBit = field.end % bitsPerRow;
    const x = margin + startBit * bitWidth;
    const y = margin + 40 + row * rowHeight;
    const width = (endBit - startBit + 1) * bitWidth;
    return { ...field, x, y, width, height: rowHeight - 4 };
  });

  const maxRow = fields.length > 0 ? Math.floor(Math.max(...fields.map(f => f.end)) / bitsPerRow) : 0;
  const width = margin * 2 + bitsPerRow * bitWidth;
  const height = margin * 2 + 40 + (maxRow + 1) * rowHeight;

  return { title, fields: layoutFields, width, height, bitsPerRow, bitWidth, margin };
}

// --- AI/ML LAYOUT FUNCTIONS ---

export function layoutAIAgent(parsedData) {
  const { title, components, connections } = parsedData;

  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", ranksep: 80, nodesep: 60, marginx: 60, marginy: 60 });
  g.setDefaultEdgeLabel(() => ({}));

  components.forEach(comp => {
    const w = Math.max(140, comp.label.length * 10);
    const h = comp.description ? 70 : 50;
    g.setNode(comp.id, { ...comp, width: w, height: h });
  });

  connections.forEach(conn => {
    g.setEdge(conn.from, conn.to, { label: conn.label, bidirectional: conn.bidirectional });
  });

  dagre.layout(g);

  const layoutComponents = components.map(c => {
    const info = g.node(c.id);
    return { ...c, x: info.x, y: info.y, width: info.width, height: info.height };
  });

  const layoutConnections = connections.map(conn => {
    const info = g.edge(conn.from, conn.to);
    return { ...conn, points: info?.points || [] };
  });

  const graphInfo = g.graph();
  return { title, components: layoutComponents, connections: layoutConnections, width: (graphInfo.width || 600) + 120, height: (graphInfo.height || 400) + 120 };
}

export function layoutAgentWorkflow(parsedData) {
  const { title, nodes, edges } = parsedData;

  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", ranksep: 60, nodesep: 50, marginx: 50, marginy: 50 });
  g.setDefaultEdgeLabel(() => ({}));

  nodes.forEach(node => {
    const w = Math.max(120, node.label.length * 9);
    const h = node.type === "decision" ? 60 : 50;
    g.setNode(node.id, { ...node, width: w, height: h });
  });

  edges.forEach(edge => {
    g.setEdge(edge.from, edge.to, { label: edge.label, type: edge.type });
  });

  dagre.layout(g);

  const layoutNodes = nodes.map(n => {
    const info = g.node(n.id);
    return { ...n, x: info.x, y: info.y, width: info.width, height: info.height };
  });

  const layoutEdges = edges.map(e => {
    const info = g.edge(e.from, e.to);
    return { ...e, points: info?.points || [] };
  });

  const graphInfo = g.graph();
  return { title, nodes: layoutNodes, edges: layoutEdges, width: (graphInfo.width || 500) + 100, height: (graphInfo.height || 400) + 100 };
}

export function layoutDAG(parsedData) {
  const { title, tasks, dependencies } = parsedData;

  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", ranksep: 70, nodesep: 50, marginx: 60, marginy: 60 });
  g.setDefaultEdgeLabel(() => ({}));

  tasks.forEach(task => {
    const w = Math.max(140, task.label.length * 9);
    g.setNode(task.id, { ...task, width: w, height: 50 });
  });

  dependencies.forEach(dep => {
    g.setEdge(dep.from, dep.to, {});
  });

  dagre.layout(g);

  const layoutTasks = tasks.map(t => {
    const info = g.node(t.id);
    return { ...t, x: info.x, y: info.y, width: info.width, height: info.height };
  });

  const layoutDeps = dependencies.map(d => {
    const info = g.edge(d.from, d.to);
    return { ...d, points: info?.points || [] };
  });

  const graphInfo = g.graph();
  return { title, tasks: layoutTasks, dependencies: layoutDeps, width: (graphInfo.width || 500) + 120, height: (graphInfo.height || 400) + 120 };
}

export function layoutPipeline(parsedData) {
  const { title, stages, flows } = parsedData;
  const stageWidth = 150;
  const stageHeight = 60;
  const gap = 80;
  const margin = 60;

  // Linear horizontal layout
  const layoutStages = stages.map((stage, i) => ({
    ...stage,
    x: margin + i * (stageWidth + gap),
    y: 120,
    width: stageWidth,
    height: stageHeight
  }));

  const stageMap = {};
  layoutStages.forEach(s => { stageMap[s.id] = s; });

  const layoutFlows = flows.map(flow => ({
    ...flow,
    fromX: stageMap[flow.from] ? stageMap[flow.from].x + stageWidth : 0,
    fromY: stageMap[flow.from] ? stageMap[flow.from].y + stageHeight / 2 : 0,
    toX: stageMap[flow.to] ? stageMap[flow.to].x : 0,
    toY: stageMap[flow.to] ? stageMap[flow.to].y + stageHeight / 2 : 0
  }));

  const width = margin * 2 + stages.length * (stageWidth + gap);
  const height = 250;

  return { title, stages: layoutStages, flows: layoutFlows, width, height };
}

export function layoutKnowledgeGraph(parsedData) {
  const { title, entities, relations } = parsedData;

  // Force-directed style layout using dagre
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "LR", ranksep: 100, nodesep: 80, marginx: 80, marginy: 80 });
  g.setDefaultEdgeLabel(() => ({}));

  entities.forEach(entity => {
    const w = Math.max(100, entity.label.length * 10);
    g.setNode(entity.id, { ...entity, width: w, height: 50 });
  });

  relations.forEach(rel => {
    g.setEdge(rel.from, rel.to, { relation: rel.relation });
  });

  dagre.layout(g);

  const layoutEntities = entities.map(e => {
    const info = g.node(e.id);
    return { ...e, x: info.x, y: info.y, width: info.width, height: info.height };
  });

  const layoutRelations = relations.map(r => {
    const info = g.edge(r.from, r.to);
    return { ...r, points: info?.points || [] };
  });

  const graphInfo = g.graph();
  return { title, entities: layoutEntities, relations: layoutRelations, width: (graphInfo.width || 600) + 160, height: (graphInfo.height || 400) + 160 };
}

export function layoutModelArch(parsedData) {
  const { title, layers, connections } = parsedData;
  const layerWidth = 180;
  const layerHeight = 50;
  const gap = 40;
  const margin = 60;

  // Vertical stack layout
  const layoutLayers = layers.map((layer, i) => ({
    ...layer,
    x: 300,
    y: margin + 40 + i * (layerHeight + gap),
    width: layerWidth,
    height: layerHeight
  }));

  const layerMap = {};
  layoutLayers.forEach(l => { layerMap[l.id] = l; });

  const layoutConnections = connections.map(conn => ({
    ...conn,
    fromX: layerMap[conn.from] ? layerMap[conn.from].x + layerWidth / 2 : 0,
    fromY: layerMap[conn.from] ? layerMap[conn.from].y + layerHeight : 0,
    toX: layerMap[conn.to] ? layerMap[conn.to].x + layerWidth / 2 : 0,
    toY: layerMap[conn.to] ? layerMap[conn.to].y : 0
  }));

  const width = 600;
  const height = margin * 2 + 40 + layers.length * (layerHeight + gap);

  return { title, layers: layoutLayers, connections: layoutConnections, width, height };
}

export function layoutSystemArch(parsedData) {
  const { title, components, connections } = parsedData;

  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: "TB", ranksep: 80, nodesep: 60, marginx: 60, marginy: 60 });
  g.setDefaultEdgeLabel(() => ({}));

  components.forEach(comp => {
    const w = Math.max(150, comp.label.length * 10);
    g.setNode(comp.id, { ...comp, width: w, height: 60 });
  });

  connections.forEach(conn => {
    g.setEdge(conn.from, conn.to, { label: conn.label, bidirectional: conn.bidirectional });
  });

  dagre.layout(g);

  const layoutComponents = components.map(c => {
    const info = g.node(c.id);
    return { ...c, x: info.x, y: info.y, width: info.width, height: info.height };
  });

  const layoutConnections = connections.map(conn => {
    const info = g.edge(conn.from, conn.to);
    return { ...conn, points: info?.points || [] };
  });

  const graphInfo = g.graph();
  return { title, components: layoutComponents, connections: layoutConnections, width: (graphInfo.width || 600) + 120, height: (graphInfo.height || 400) + 120 };
}

